import { AustralianStateCode } from "./matchLocationToState.util";

/*-- Resolves a coordinate to a state locally, rather than through Google's geocoding web service.
     That service rejects the site's API key outright ("API keys with referer restrictions cannot be
     used with this API"), and because it answers with HTTP 200 the failure was invisible: every
     visitor fell through to the first contact card. Australia's borders are mostly meridians and
     parallels, so the lookup does not need an API at all — and this way it cannot be broken by a key
     restriction, a quota, or a blocked network. --*/

// Continental bounds plus Tasmania. Anything outside resolves to undefined, as an overseas visitor did before.
const AUSTRALIA_BOUNDS = { minLat: -43.9, maxLat: -9.0, minLon: 112.5, maxLon: 154.2 };

// WA | NT and WA | SA.
const WA_BORDER_LON = 129.0;
// NT | QLD.
const NT_QLD_BORDER_LON = 138.0;
// SA | QLD, SA | NSW and SA | VIC.
const SA_EAST_BORDER_LON = 141.0;
// NT | SA and NT | QLD in the north, SA | QLD in the east.
const NORTHERN_BORDER_LAT = -26.0;
// Bass Strait. Wilsons Promontory, the southernmost point of the mainland, sits at -39.14.
const TAS_BORDER_LAT = -39.2;

type BorderPoint = [lon: number, lat: number];

/*-- NSW | QLD: the 29th parallel as far as the Dumaresq, then the ranges out to Point Danger. --*/
const NSW_QLD_BORDER: BorderPoint[] = [
  [141.0, -29.0],
  [148.95, -28.95],
  [150.5, -28.6],
  [151.5, -28.9],
  [152.5, -28.3],
  [153.55, -28.17],
];

/*-- VIC | NSW: the Murray from the South Australian border, then the straight Black-Allan line from
     the river's source to Cape Howe. --*/
const VIC_NSW_BORDER: BorderPoint[] = [
  [141.0, -34.1],
  [142.2, -34.17],
  [143.55, -35.35],
  // The river swings south to Echuca, back north through Tocumwal, then south again past Albury.
  [144.75, -36.13],
  [145.6, -35.85],
  [146.0, -36.0],
  [146.9, -36.09],
  [147.9, -36.18],
  [148.19, -36.51],
  [149.0, -36.9],
  [149.97, -37.5],
];

/*-- The ACT is an enclave inside NSW, so it has to be tested before the NSW/VIC split. Its real
     eastern border is a run of survey lines rather than a meridian, so this box is drawn to take in
     Canberra while leaving Queanbeyan in NSW; a point within a few kilometres of that border may
     resolve either way. Authors almost always group the two ("NSW & ACT"), which absorbs the
     difference — see matchLocationToState for how a card's location is matched. --*/
const ACT_BOUNDS = { minLat: -35.95, maxLat: -35.12, minLon: 148.76, maxLon: 149.21 };

/*-- Latitude of a border polyline at the given longitude, clamped to its end points. --*/
const borderLatitudeAt = (border: BorderPoint[], longitude: number): number => {
  const [firstLon, firstLat] = border[0];
  const [lastLon, lastLat] = border[border.length - 1];

  if (longitude <= firstLon) {
    return firstLat;
  }

  if (longitude >= lastLon) {
    return lastLat;
  }

  for (let index = 1; index < border.length; index += 1) {
    const [startLon, startLat] = border[index - 1];
    const [endLon, endLat] = border[index];

    if (longitude <= endLon) {
      const ratio = (longitude - startLon) / (endLon - startLon);

      return startLat + ratio * (endLat - startLat);
    }
  }

  return lastLat;
};

const isWithin = (
  bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number },
  latitude: number,
  longitude: number,
): boolean =>
  latitude >= bounds.minLat && latitude <= bounds.maxLat && longitude >= bounds.minLon && longitude <= bounds.maxLon;

/*-- A larger latitude is further north, so "north of a border" reads as latitude > borderLatitude.
     Accurate to the border itself except within a kilometre or two of the Murray and of the ranges
     along the NSW/QLD border, which is well inside what routing an enquiry to a state manager needs. --*/
const resolveStateFromCoordinates = (latitude: number, longitude: number): AustralianStateCode | undefined => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined;
  }

  if (!isWithin(AUSTRALIA_BOUNDS, latitude, longitude)) {
    return undefined;
  }

  if (latitude <= TAS_BORDER_LAT) {
    return "TAS";
  }

  if (longitude < WA_BORDER_LON) {
    return "WA";
  }

  if (latitude > NORTHERN_BORDER_LAT) {
    return longitude < NT_QLD_BORDER_LON ? "NT" : "QLD";
  }

  if (longitude < SA_EAST_BORDER_LON) {
    return "SA";
  }

  if (latitude > borderLatitudeAt(NSW_QLD_BORDER, longitude)) {
    return "QLD";
  }

  if (isWithin(ACT_BOUNDS, latitude, longitude)) {
    return "ACT";
  }

  return latitude > borderLatitudeAt(VIC_NSW_BORDER, longitude) ? "NSW" : "VIC";
};

export default resolveStateFromCoordinates;
